import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererStyleFlags2
} from '@angular/core';
import { AuthService } from './services/auth/auth-service';
import { DOCUMENT } from '@angular/common';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { GlobalStateProviderService } from './services/global-state-provider.service';
import { Service } from './models/service';
import { filter, Subscription, take } from 'rxjs';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { EventType, Router } from '@angular/router';
import { Token } from './models/token';
import { WebsocketService } from './services/websocket.service';
import { WebSocketMessageType } from './enums';
import { WsMessage } from './models/web-socket-message';
import { JwtHelperService } from '@auth0/angular-jwt';
import { FormioToken } from './models/formio-token';
import { Title } from '@angular/platform-browser';
import { BPASettings } from '@models/bpa-settings';
import { BpaSettingsService } from './services/bpa-settings.service';
import { FaviconService } from './services/favicon.service';
import { ColombiaIframeService } from './services/colombia-iframe.service';
import { DarkThemeService } from './services/dark-theme.service';
import { ScrollDirectionService } from './services/scroll-direction.service';
import { FluidColumnsStackService } from './services/fluid-columns-stack.service';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { EregistrationsTranslationService } from './services/eregistrations-translation.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';

declare const require: any;
const { version: appVersion } = require('../../package.json');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  isLoaded = false;
  title = 'ds-frontend';
  service: Service | null = null;
  appVersion: string | null = null;
  private subscription?: Subscription;
  private bpaSettingsSubscription?: Subscription;
  private bpaSettingsRefreshInterval?: any;
  private updateToastr?: ActiveToast<any>;
  lastActiveTime: number = Date.now();
  inactiveTimeout: number = environment.PAGE_INACTIVITY_JWT_CHECK_MILLIS;
  configuratorEnabled: boolean = environment.CONFIGURATOR_ENABLED;
  private readonly jwtHelperService: JwtHelperService = new JwtHelperService();
  isPartBDetailView: boolean = false;
  /** PDF chrome-url-to-pdf route: skip shell and BPA loading gate */
  isPrintRoute: boolean = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    public authService: AuthService,
    private readonly http: HttpClient,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly globalStateProviderService: GlobalStateProviderService,
    private readonly webSocket: WebsocketService,
    private readonly renderer: Renderer2,
    private readonly bpaSettings: BpaSettingsService,
    private readonly titleService: Title,
    private readonly faviconService: FaviconService,
    private readonly iframeService: ColombiaIframeService,
    private readonly darkThemeService: DarkThemeService,
    private readonly scrollDirectionService: ScrollDirectionService,
    // TOBE-18004: self-starting — injected so empty filler columns get their
    // `cols-stacked` cue app-wide, wherever formio renders a columns row.
    private readonly fluidColumnsStackService: FluidColumnsStackService,
    private readonly localeService: BsLocaleService,
    private readonly eregistrationsTranslationService: EregistrationsTranslationService,
    private readonly googleAnalyticsService: GoogleAnalyticsService
  ) {
    // Configure datepicker to start week on Monday (ISO 8601 standard)
    if (enGbLocale.week) {
      enGbLocale.week.dow = 1; // Monday = 1
    }
    defineLocale('en-gb', enGbLocale);
    this.localeService.use('en-gb');
  }

  ngOnInit() {
    this.googleAnalyticsService.init(
      environment.GA_MEASUREMENT_ID ?? '',
      environment.IS_IFRAME_APP === true ||
        this.iframeService.isRunningInIframe()
    );

    // Store base translations (loaded by app initializer) in the service
    this.eregistrationsTranslationService.setBaseTranslations(
      window.GLOBAL_TRANSLATIONS ?? {}
    );
    this.setResponsiveVerticalTabsCssTranslations();

    this.appVersion = appVersion;
    this.renderer.addClass(
      this.document.documentElement,
      'instance-' + environment.SYSTEM_CODE
    );
    this.loadStyle('ds-default-theme.css', 'default-style');
    this.loadStyle(`${environment.SYSTEM_CODE}_STYLE.css`, 'instance-style');
    this.globalStateProviderService
      .getServiceChanged()
      .subscribe((service: any) => {
        this.service = service;
      });

    // Track route changes for shell visibility (Part B / Inspector / print PDF)
    this.updateShellRouteFlags(this.router.url);
    this.router.events
      .pipe(filter((event) => event.type === EventType.NavigationEnd))
      .subscribe(() => {
        this.updateShellRouteFlags(this.router.url);
      });

    // Try auto-login if user is already authenticated with Keycloak from another service
    if (
      this.authService.keycloakActive &&
      !this.authService.isLoggedIn$.value
    ) {
      this.authService
        .checkSsoAndLogin()
        .then(() => {
          // Auto-login completed
        })
        .catch((err) => {
          console.error('Auto-login failed:', err);
        });
    }

    // Try auto-login if user is already authenticated with CAS from another service
    if (
      !this.authService.keycloakActive &&
      !this.authService.isLoggedIn$.value
    ) {
      this.authService
        .checkSsoAndLogin()
        .then(() => {
          // Auto-login completed
        })
        .catch((err) => {
          console.error('Auto-login failed (CAS):', err);
        });
    }

    this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      if (isLoggedIn && environment.WEBSOCKET_INTEGRATION_ACTIVE) {
        this.connectWebsocket();
      }
    });
    // FormIO payment forms require this
    window.GLOBALS = {
      TRANSACTION_STATUS: {
        OK: 1,
        FAIL: 2,
        ERROR: 3,
        PENDING: 4,
        NOT_STARTED: 5
      },
      CUBAN_PRODUCT_LIST_URL: environment.CUBAN_PRODUCT_LIST_URL,
      CUBAN_PRODUCT_LIST_DEBUG_LOGGING:
        environment.CUBAN_PRODUCT_LIST_DEBUG_LOGGING
    };

    this.document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    this.document.addEventListener(
      'visibilitychange',
      this.handleAutoLogoutWhenTokenIsExpired.bind(this)
    );

    this.router.events
      .pipe(filter((event) => event.type === EventType.NavigationStart))
      .subscribe(() => this.handleAutoLogoutWhenTokenIsExpired());

    this.faviconService.loadFavicon().subscribe({
      next: (blob) => {
        if (blob?.size > 0) {
          const objectUrl = URL.createObjectURL(blob);
          this.setFavicon('icon', objectUrl);
        } else {
          this.setFavicon('icon', 'favicon.ico');
        }
      },
      error: () => {
        this.setFavicon('icon', 'favicon.ico');
      }
    });
    this.bpaSettings.loadBpaSettings().subscribe((bpaSettings: BPASettings) => {
      this.titleService.setTitle(bpaSettings.siteName ?? '');
      this.isLoaded = true;
    });

    this.bpaSettingsSubscription = this.bpaSettings
      .getBpaSettings$()
      .subscribe((bpaSettings: BPASettings | null) => {
        if (bpaSettings) {
          this.titleService.setTitle(bpaSettings.siteName ?? '');
        }
      });
    if (environment.BPA_SETTINGS_CACHE_DURATION_MS) {
      this.bpaSettingsRefreshInterval = setInterval(() => {
        this.bpaSettings.refreshBpaSettings().subscribe();
      }, environment.BPA_SETTINGS_CACHE_DURATION_MS);
    } else {
      this.bpaSettings.refreshBpaSettings().subscribe();
    }

    if (this.iframeService.isRunningInIframe()) {
      this.document.documentElement.setAttribute('embedded', '');
    }
  }

  private setResponsiveVerticalTabsCssTranslations(): void {
    this.setRootCssStringVariable(
      '--responsive-vertical-tab-toggle-text',
      $localize`:@@responsiveVerticalTabToggleText:Navigation`
    );
    this.setRootCssStringVariable(
      '--responsive-vertical-tab-drawer-title',
      $localize`:@@responsiveVerticalTabDrawerTitle:Navigate to`
    );
  }

  private setRootCssStringVariable(name: string, value: string): void {
    this.renderer.setStyle(
      this.document.documentElement,
      name,
      JSON.stringify(value),
      RendererStyleFlags2.DashCase
    );
  }

  ngOnDestroy() {
    this.document.removeEventListener(
      'visibilitychange',
      this.handleAutoLogoutWhenTokenIsExpired
    );
    this.document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.bpaSettingsSubscription) {
      this.bpaSettingsSubscription.unsubscribe();
    }

    if (this.bpaSettingsRefreshInterval) {
      clearInterval(this.bpaSettingsRefreshInterval);
    }
  }

  handleAutoLogoutWhenTokenIsExpired() {
    const token = localStorage.getItem(Token.tokenNameInStorage) ?? '';

    if (!token) return;

    if (environment.ENABLE_TOKEN_INTROSPECT) {
      const tokenHint =
        this.authService.keycloak?.getKeycloakInstance()?.idToken;

      this.http
        .post<{ active: boolean }>(
          `${environment.BASE_URL}api/v1/validate-token/`,
          {
            token: token,
            kc_id_token_hint: tokenHint ?? null
          },
          {
            observe: 'response',
            withCredentials: false
          }
        )
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            const responseData = response.body;
            if (!responseData?.active) {
              this.authService.logout(window.location.origin);
              this.toastr.info(
                $localize`:@@Session expired:Your session has expired`
              );
            }
          },
          error: (error) => {
            if (error.status === 401 || error.status === 403) {
              this.authService.logout(window.location.origin);
              this.toastr.info(
                $localize`:@@Session expired:Your session has expired`
              );
            }
          }
        });
    }

    const isTokenExpired = this.jwtHelperService.isTokenExpired(token);

    if (!isTokenExpired) return;

    this.authService.logout(window.location.origin);
    this.toastr.info(
      $localize`:@@Your session is expired:Your session is expired`
    );
  }

  handleVisibilityChange = () => {
    if (this.document.hidden) {
      this.checkAndRefreshTokenIfNeeded();
    } else {
      this.lastActiveTime = Date.now();
    }
  };

  /**
   * Checks inactivity duration and refreshes token if needed
   */
  private checkAndRefreshTokenIfNeeded(): void {
    const currentTime = Date.now();
    const inactiveDuration = currentTime - this.lastActiveTime;

    if (inactiveDuration <= this.inactiveTimeout) {
      return;
    }

    if (environment.DEBUG_MODE) {
      console.log('Checking if JWT needs a pick-me-up');
    }

    if (this.authService.keycloakActive) {
      this.refreshKeycloakToken();
    } else {
      this.refreshCasToken();
    }
  }

  /**
   * Refreshes Keycloak token and persists to localStorage
   */
  private refreshKeycloakToken(): void {
    this.authService.keycloak.updateToken().then((updated) => {
      if (!updated) {
        return;
      }

      this.authService.keycloak.getToken().then((currentToken) => {
        localStorage.setItem(Token.tokenNameInStorage, currentToken);
        localStorage.setItem(
          FormioToken.tokenNameInStorage,
          FormioToken.scheme + currentToken
        );
        localStorage.setItem(
          Token.refreshTokenNameInStorage,
          this.authService.keycloak.getKeycloakInstance().refreshToken ?? ''
        );
      });
    });
  }

  /**
   * Refreshes CAS token if expired
   */
  private refreshCasToken(): void {
    const token = localStorage.getItem(Token.tokenNameInStorage) ?? '';
    if (!token) {
      return;
    }

    if (!this.jwtHelperService.isTokenExpired(token, 60 * 5)) {
      return;
    }

    const refreshToken =
      localStorage.getItem(Token.refreshTokenNameInStorage) ?? '';
    this.authService.cas.updateTokenSubject.next(refreshToken);
  }

  loadStyle(styleName: string, id: string) {
    const head = this.document.getElementsByTagName('head')[0];

    let instanceStyle = this.document.getElementById(id) as HTMLLinkElement;
    if (instanceStyle) {
      instanceStyle.href = styleName;
    } else {
      const style = this.document.createElement('link');
      style.id = id;
      style.rel = 'stylesheet';
      style.href = `${styleName}`;

      head.appendChild(style);
    }
  }

  private connectWebsocket() {
    // isLoggedIn$ re-emits on every token refresh; the service reconnects by itself.
    if (this.subscription) return;
    this.subscription = this.webSocket.messageReceived$.subscribe({
      next: (message: WsMessage) => {
        if (!message) return;
        if (!this.updateToastr?.toastId) {
          switch (message.type) {
            case WebSocketMessageType.NEW_MESSAGE:
            case WebSocketMessageType.NEW_ALERT_MESSAGE: {
              // $localize (not a GLOBAL_TRANSLATIONS lookup) so `ng extract-i18n`
              // can see these keys and keep them in the registry. The app
              // initializer feeds the same catalogue to loadTranslations(),
              // so resolution and the English fallback are unchanged.
              const newMsgText = $localize`:@@newMessage:You have new message`;
              const clickText = $localize`:@@clickToGoToMessages:Click to go to messages.`;
              this.updateToastr = this.toastr.info(
                newMsgText + '</br>' + clickText,
                undefined,
                { timeOut: 10000, tapToDismiss: false, enableHtml: true }
              );
              this.updateToastr.onTap.subscribe(() => {
                this.clearUpdateToastr();
                this.globalStateProviderService.openMessagesDialog();
              });
              break;
            }
            case WebSocketMessageType.FILE_STATE_UPDATE: {
              break;
            }
            default:
          }
          this.updateToastr?.onHidden.subscribe(() => {
            this.clearUpdateToastr();
          });
        }
      }
    });
    this.webSocket.connect();
  }

  private clearUpdateToastr(): void {
    if (!this.updateToastr) return;
    this.toastr.clear(this.updateToastr.toastId);
    this.updateToastr = undefined;
  }

  private onUpdateToastrClick(gotToPath: string): void {
    // Reloading has issues because of formArray update in categories component
    this.clearUpdateToastr();
    this.router.navigateByUrl(gotToPath);
  }

  private setFavicon(rel: string, href: string) {
    // Treat any scheme (e.g., http:, https:, data:, blob:) as absolute, and also protocol-relative URLs (//)
    const isAbsolute =
      /^[a-z][a-z0-9+\-.]*:/i.test(href) || href.startsWith('//');
    const alreadyPrefixed = href.startsWith(environment.BASE_URL);
    let resolvedHref = href;
    if (!isAbsolute && !alreadyPrefixed) {
      if (href.startsWith('/')) {
        resolvedHref = environment.BASE_URL.replace(/\/$/, '') + href;
      } else {
        resolvedHref = environment.BASE_URL + href;
      }
    }

    // Cache-bust so browsers actually reload the favicon
    const cacheBust = (u: string) => {
      try {
        const url = new URL(u, window.location.origin);
        // Do not cache-bust for blob: or data: URLs
        if (url.protocol === 'blob:' || url.protocol === 'data:') {
          return u;
        }
        url.searchParams.set('v', Date.now().toString());
        return url.toString();
      } catch {
        // If parsing fails (e.g., relative path), apply cache bust unless it's a blob:/data: URL string
        if (/^(?:blob|data):/i.test(u)) {
          return u;
        }
        const sep = u.includes('?') ? '&' : '?';
        return `${u}${sep}v=${Date.now()}`;
      }
    };
    resolvedHref = cacheBust(resolvedHref);

    // Determine appropriate MIME type from extension
    const lower = href.toLowerCase();
    let type = '';
    if (lower.endsWith('.png')) type = 'image/png';
    else if (lower.endsWith('.svg') || lower.includes('image/svg+xml'))
      type = 'image/svg+xml';
    else if (lower.endsWith('.ico')) type = 'image/x-icon';

    // Always target rel="icon"
    let linkElement = this.document.querySelector('link[rel="icon"]');

    if (!linkElement) {
      linkElement = this.document.createElement('link');
      this.renderer.setAttribute(linkElement, 'rel', 'icon');
      this.renderer.appendChild(this.document.head, linkElement);
    }

    this.renderer.setAttribute(linkElement, 'href', resolvedHref);
    if (type) this.renderer.setAttribute(linkElement, 'type', type);
  }

  private updateShellRouteFlags(url: string): void {
    this.isPrintRoute = url.startsWith('/print/');
    this.isPartBDetailView =
      url.startsWith('/part-b/') || url.startsWith('/inspector/');
  }
}
